<script lang="ts" setup>
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { useElementVisibility } from '@vueuse/core';
import { type HotelRecommendationDto } from '@clients/HotelApi';
import { toAbsoluteSrc } from '@store/content';

const { hotels } = defineProps<{
    title: string;
    hotels: HotelRecommendationDto[];
}>();

const emit = defineEmits<{
    select: [string];
}>();

const analyticsStore = useAnalyticsStore();

const defaultImgUrl = toAbsoluteSrc(
    '/uploads/hotel_placeholder_v4_af8e033460.png'
);

const containerEl = ref<HTMLDivElement>();
const visibilityEventSent = ref(false);
const isVisible = useElementVisibility(containerEl);

watchEffect(() => {
    if (hotels && !visibilityEventSent.value && isVisible.value) {
        analyticsStore.trackAll(
            'recommended-hotel-visible',
            analyticsStore.searchResultsAttributes
        );
        visibilityEventSent.value = true;
    }
});
</script>

<template>
    <div
        v-if="hotels.length"
        ref="containerEl"
        class="hotel-similar-card-section"
    >
        <div class="hotel-card-section-header">
            <mr-typography weight="700" size="display4" color="white">
                هتل‌های مشابه {{ title }}
            </mr-typography>
        </div>
        <div class="hotel-card-container">
            <div
                v-for="hotel in hotels"
                :key="hotel.id"
                class="hotel-simple-card"
                @click="emit('select', hotel.englishName ?? '')"
            >
                <MrBlurHashImage
                    class="hotel-img"
                    image-fit="cover"
                    :width="200"
                    :height="120"
                    :src="hotel.photos?.[0].large ?? defaultImgUrl"
                    :hash="hotel.photos?.[0].blurhash ?? ''"
                    :alt="hotel.photos?.[0].alt ?? 'hotel-img'"
                />
                <div class="hotel-card-detail">
                    <mr-typography class="name" weight="700">
                        هتل {{ hotel.name }}
                    </mr-typography>
                    <div class="hotel-detail">
                        <div class="label">
                            <mr-typography
                                weight="500"
                                color="var(--blue-700)"
                                size="small"
                            >
                                {{ hotel.label }}
                            </mr-typography>
                        </div>
                        <div class="raing">
                            <mr-typography
                                weight="700"
                                color="var(--cool-gray-800)"
                                size="small"
                            >
                                {{ hotel.stars }}
                            </mr-typography>
                            <mr-icon
                                size="x-small"
                                :icon="faStar"
                                color="var(--yellow-400)"
                            />
                        </div>
                    </div>
                </div>
                <mr-divider :thickness="1" />
                <div class="price-section">
                    <mr-typography size="small" color="var(--cool-gary-600)">
                        قیمت هرشب از
                    </mr-typography>

                    <mr-price-re
                        class="hotel-price"
                        :price="hotel.price ?? 0"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.hotel-similar-card-section {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    background-color: usePalette($p-blue, $p-400);
    padding-block: 12px;

    @include desktop-only {
        border-radius: 12px;
    }

    .hotel-card-section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding-inline: 12px;
    }
    .hotel-card-container {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
        align-items: center;
        justify-content: start;
        gap: 12px;
        width: 100%;
        overflow: scroll;
        @include no-scrollbar();
        .hotel-img {
            flex-shrink: 0;
        }
        .hotel-simple-card {
            width: 160px;
            height: 100%;
            display: flex;
            flex-direction: column;
            background-color: white;
            cursor: pointer;
            border-radius: 12px;
            overflow: hidden;
            flex-shrink: 0;
            @include desktop-only {
                width: 185px;
            }
            &:first-child {
                margin-right: 12px;
            }
            &:last-child {
                margin-left: 12px;
            }
            .hotel-card-detail {
                display: flex;
                flex-direction: column;
                gap: 6px;
                padding: 6px 12px;
                height: 100%;
                .name {
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;
                    line-clamp: 2;
                    overflow: hidden;
                    height: 100%;
                }
                .hotel-detail {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    .label {
                        padding: 2px 8px;
                        border-radius: 8px;
                        background-color: usePalette($p-blue, $p-100);
                    }
                    .raing {
                        display: flex;
                        align-items: center;
                    }
                }
            }

            .hotel-price-section {
                display: flex;
                flex-direction: column;
            }

            .price-section {
                display: flex;
                flex-direction: column;
                .hotel-price {
                    align-self: flex-end;
                }

                padding: 6px 12px;
            }
        }
    }
}
</style>
